import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';

import { fetchBlueprint } from 'state';
import {
  VerdictBanner,
  countsLine,
  gateShortLabel,
  readAnswerKeyGate,
  readCoverage,
} from 'components';

import { ValidityTag } from '../ScenarioBuilder/ValidityPanel';
import {
  ConflictNotice,
  JobOutcomePanel,
  JobStallNotice,
  useJobRun,
} from '../ScenarioBuilder/JobRun';
import { JsonObject } from '../ScenarioBuilder/types';
import { AnswerKeyCoverageDocument } from './api';
import AssessmentRegenerationStatus from './AssessmentRegenerationStatus';
import './blueprintAnswerKey.css';

/** Automatic updates are authoritative when supplied by the server. Legacy
 * manual tools remain available for blueprints outside that workflow. */

/** The eight characters of the identity hash a human compares by eye (D5). */
const shortHash = (hash?: string | null) => String(hash || '').slice(0, 8);

const formatMoment = (value?: string | null) => {
  if (!value) return '';
  const moment = new Date(value);
  return Number.isNaN(moment.getTime()) ? '' : moment.toLocaleDateString();
};

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  draft: 'default',
  retired: 'red',
};

type Props = {
  blueprintId: number;
  variantId?: number;
  versionNumber?: number;
  templateSessionId?: number | null;
  document: AnswerKeyCoverageDocument | null;
  loading?: boolean;
  error?: string;
  /** Re-read the coverage document once a derivation has succeeded. */
  onRegenerated?: () => void;
  onOpenDetails?: () => void;
  onRetry?: () => void;
  retrying?: boolean;
  /** The selected version was launched: its remedy is a new version. */
  versionLocked?: boolean;
  onNewVersion?: () => void;
};

const AnswerKeyPanel: React.FC<Props> = ({
  blueprintId,
  variantId,
  versionNumber,
  templateSessionId,
  document: coverageDocument,
  loading,
  error,
  onRegenerated,
  onOpenDetails,
  onRetry,
  retrying,
  versionLocked,
  onNewVersion,
}) => {
  const dispatch = useDispatch();
  const {
    job,
    running,
    error: jobError,
    conflict,
    stall,
    elapsedMs,
    run,
  } = useJobRun();

  const automatic = coverageDocument?.regeneration?.automatic;
  const key = coverageDocument?.key || null;
  const hasKey = Boolean(key);
  const status = String(key?.status || '').toLowerCase();

  // The key document is shaped so the shared reader can take it directly, so
  // this panel, the launch modal and the trainer's report cannot disagree
  // about what the audit found.
  const gate = useMemo(() => readAnswerKeyGate(key || undefined), [key]);
  // COUNTED AGAINST WHAT? A fraction of graded instruments is a fraction of
  // a marking scheme, and this panel's other sentence is "No answer key has
  // been derived for this version yet". The endpoint counts the exercise's
  // instruments whether or not a key exists, so the panel asks the question
  // the launch modal asks (LaunchScenarioModal): is there a key at all.
  const coverage = useMemo(
    () => (coverageDocument?.key ? readCoverage(coverageDocument) : undefined),
    [coverageDocument],
  );

  const launchCheck = coverageDocument?.launch_check;
  const wouldLaunch = launchCheck?.would_launch !== false;
  // The refusal carries its own remedy (the admin's journey, step 2): the
  // sentence says what is wrong, the button next to it is the way out.
  const refusalCode = String(launchCheck?.code || '');
  const differences = coverageDocument?.identity_difference || [];

  const payload: JsonObject = useMemo(() => {
    const body: JsonObject = { blueprint_id: blueprintId };
    if (versionNumber != null) body.version_number = versionNumber;
    if (templateSessionId != null) body.template_session_id = templateSessionId;
    return body;
  }, [blueprintId, versionNumber, templateSessionId]);

  const derive = async (extra: JsonObject = {}) => {
    const finished = await run('answer_key.derive', { ...payload, ...extra });
    if (finished && String(finished.status).toUpperCase() === 'SUCCEEDED') {
      onRegenerated?.();
      dispatch(
        fetchBlueprint({
          id: blueprintId,
          summary_scope: 'latest',
          summary_variant_id: variantId,
        }),
      );
    }
  };

  const refusal = job?.refusal || {};
  const cacheMiss =
    job?.status === 'REFUSED' && refusal.code === 'encoding_cache_miss';
  // The coverage fix (the admin's journey, step 3): instruments the matrix
  // has no column for get one from the page, through the same extension the
  // automatic chain runs, with the same spend confirmation the encoder uses.
  const proposalSpend =
    job?.status === 'REFUSED' && refusal.code === 'model_proposal_spend';
  const byReason = coverageDocument?.counts?.by_reason || {};
  const uncovered =
    Number(byReason.no_registry_concept || 0) + Number(byReason.no_column || 0);
  const extend = async (extra: JsonObject = {}) => {
    const finished = await run('matrix.extend', {
      ...payload,
      propose: true,
      model: coverageDocument?.proposal_model || '',
      ...extra,
    });
    if (finished && String(finished.status).toUpperCase() === 'SUCCEEDED') {
      onRegenerated?.();
      dispatch(
        fetchBlueprint({
          id: blueprintId,
          summary_scope: 'latest',
          summary_variant_id: variantId,
        }),
      );
    }
  };

  return (
    <Card
      className="bp-answer-key"
      size="small"
      title="Answer key"
      extra={
        <Space size={8} wrap>
          {!automatic && (
            <Popconfirm
              title={
                hasKey
                  ? 'Regenerate this answer key?'
                  : 'Derive the answer key for this version?'
              }
              description={
                hasKey
                  ? 'It derives the key again from what this version holds now — the instruments a started session can trade, the headlines already read, and the matrix columns in use — and retires the key in service. The new one only starts grading if it passes its quality audit.'
                  : 'It reads what a started session can trade, the headlines already read and the matrix columns in use, and leaves a key that only starts grading if it passes its quality audit.'
              }
              okText={hasKey ? 'Regenerate now' : 'Derive now'}
              cancelText="Cancel"
              onConfirm={() => derive()}
            >
              <Button
                type={hasKey ? 'default' : 'primary'}
                loading={running}
                disabled={running}
              >
                {hasKey ? 'Regenerate' : 'Derive the answer key'}
              </Button>
            </Popconfirm>
          )}
          <Button onClick={() => onOpenDetails?.()} disabled={!onOpenDetails}>
            Details
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <AssessmentRegenerationStatus
          regeneration={coverageDocument?.regeneration}
          onRetry={onRetry}
          retrying={retrying}
        />
        {error && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={error}
          />
        )}

        {loading && !coverageDocument && <Spin size="small" />}

        {!hasKey && !loading && !automatic && (
          <Typography.Text>
            No answer key has been derived for this version yet
          </Typography.Text>
        )}

        {hasKey && key && (
          <>
            <Space size={8} wrap align="center">
              <Typography.Text strong>{key.code}</Typography.Text>
              {key.name && (
                <Typography.Text type="secondary">{key.name}</Typography.Text>
              )}
              {status && (
                <Tag color={STATUS_COLOR[status] || 'default'}>
                  {status.toUpperCase()}
                </Tag>
              )}
              <ValidityTag
                validity={{
                  verdict: gate?.verdict || '',
                  current: !gate?.stale,
                  overridden: Boolean(gate?.overridden),
                }}
                active={status === 'active'}
              />
              {gate && (
                <Typography.Text type="secondary">
                  {gateShortLabel(gate)}
                </Typography.Text>
              )}
            </Space>

            <Typography.Text type="secondary">
              {[
                key.derived_by ? `derived by ${key.derived_by}` : '',
                formatMoment(key.derived_at)
                  ? `on ${formatMoment(key.derived_at)}`
                  : '',
                coverageDocument?.params_version || '',
                shortHash(key.identity_hash),
                key.model || '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography.Text>

            {status === 'retired' && (
              <Typography.Text type="secondary">
                {[
                  formatMoment(key.retired_at)
                    ? `Retired on ${formatMoment(key.retired_at)}`
                    : 'Retired',
                  key.superseded_by
                    ? `replaced by ${key.superseded_by}`
                    : 'nothing has replaced it yet',
                ].join(' · ')}
              </Typography.Text>
            )}
          </>
        )}

        {countsLine(coverage) && (
          <Typography.Text className="bp-answer-key__counts">
            {countsLine(coverage)}
          </Typography.Text>
        )}

        {/* The launch invariant (D3), in the words the launch itself would
            answer with. An admin should never have to click Launch to find
            out that it is refused. */}
        {!wouldLaunch && (
          <Space direction="vertical" size={6}>
            <Typography.Text strong className="bp-answer-key__refusal">
              {`Launch will be refused: ${
                launchCheck?.refusal ||
                'this variant does not meet the launch invariant.'
              }`}
            </Typography.Text>
            {refusalCode === 'identity_mismatch' &&
              versionLocked &&
              onNewVersion && (
                <Button size="small" type="primary" onClick={onNewVersion}>
                  New version from v{versionNumber}
                </Button>
              )}
            {refusalCode === 'identity_mismatch' &&
              !versionLocked &&
              automatic &&
              onRetry && (
                <Button
                  size="small"
                  type="primary"
                  onClick={onRetry}
                  loading={retrying}
                >
                  Regenerate the answer key
                </Button>
              )}
            {refusalCode === 'not_audited' && onOpenDetails && (
              <Button size="small" onClick={onOpenDetails}>
                Review the audit
              </Button>
            )}
            {refusalCode === 'launch_blocked' && onOpenDetails && (
              <Button size="small" onClick={onOpenDetails}>
                Review the withdrawal
              </Button>
            )}
          </Space>
        )}

        {uncovered > 0 && (
          <Space direction="vertical" size={4}>
            <Typography.Text type="secondary">
              {`${uncovered} instrument${uncovered === 1 ? '' : 's'} the matrix has no column for. Extending the matrix derives a column where a rule or the model can, checks it against PM's prices, and derives the answer key again.${
                versionLocked
                  ? ' This version was launched, so the new columns grade from its next version.'
                  : ''
              }`}
            </Typography.Text>
            <Button
              size="small"
              type="primary"
              ghost
              disabled={running}
              onClick={() => extend()}
            >
              {`Extend grading to ${uncovered} ungraded instrument${uncovered === 1 ? '' : 's'}`}
            </Button>
          </Space>
        )}
        {differences.length > 0 && (
          <Space direction="vertical" size={2}>
            <Typography.Text type="secondary">
              What changed since this key was derived
            </Typography.Text>
            {differences.map(sentence => (
              <Typography.Text key={sentence}>{sentence}</Typography.Text>
            ))}
          </Space>
        )}

        <VerdictBanner gate={gate} />

        <ConflictNotice job={conflict} />
        <JobStallNotice stall={stall} />
        {jobError && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={jobError}
          />
        )}
        <JobOutcomePanel
          job={job}
          successTitle={
            job?.kind === 'matrix.extend'
              ? 'The matrix was extended; the answer key derives again'
              : 'The answer key was derived'
          }
          elapsedMs={elapsedMs}
        >
          {cacheMiss && !automatic && (
            <Popconfirm
              title="Read the missing headlines with the model?"
              description="This calls the model for every headline the cache does not already hold, which costs money. The run is recorded against your name."
              okText="Spend and derive"
              cancelText="Cancel"
              onConfirm={() =>
                derive({
                  ...(refusal.confirm_with || { encode: true }),
                  acknowledged_refusal: String(refusal.code || ''),
                })
              }
            >
              <Button size="small" style={{ marginTop: 8 }}>
                Read the headlines with the model (spends)
              </Button>
            </Popconfirm>
          )}
          {proposalSpend && (
            <Popconfirm
              title="Ask the model to propose the missing columns?"
              description="One call per instrument offered. Every answer is checked against PM's own prices before it grades anything, and the run is recorded against your name."
              okText="Spend and extend"
              cancelText="Cancel"
              onConfirm={() =>
                extend({
                  ...(refusal.confirm_with || {}),
                  acknowledged_refusal: String(refusal.code || ''),
                })
              }
            >
              <Button size="small" style={{ marginTop: 8 }}>
                Propose the columns with the model (spends)
              </Button>
            </Popconfirm>
          )}
        </JobOutcomePanel>

        {variantId != null && (
          <Typography.Text type="secondary" className="bp-answer-key__muted">
            {`Variant ${variantId}${
              templateSessionId
                ? ` · template session ${templateSessionId}`
                : ''
            }`}
          </Typography.Text>
        )}
      </Space>
    </Card>
  );
};

export default AnswerKeyPanel;
